import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TileComponent, EmptyStateComponent, TagComponent } from '@tecalisdev/infinity';
import { OperationPolChecksCompareInfoComponent } from '../../../../../shared/components/operations/pol/operation-pol-checks-compare-info/operation-pol-checks-compare-info.component';
import {
	CHECK,
	IOperationPolDetail,
	IProcessDetailResponse,
	PROCESS_STATUS,
} from '../../../../../processes/models/processes.models';
import { IOperationDetailResponse } from '../../../../models/operations.models';
import { OperationPolChecksAntifraudComponent } from '../../../../../shared/components/operations/pol/operation-pol-checks-antifraud/operation-pol-checks-antifraud.component';
import { ProcessHelperService } from '../../../../../processes/services/process-helper.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';

@Component({
	selector: 'app-operation-detail-tabs-checks-pol',
	imports: [
		TranslatePipe,
		TileComponent,
		TagComponent,
		OperationPolChecksCompareInfoComponent,
		OperationPolChecksAntifraudComponent,
		EmptyStateComponent,
	],
	templateUrl: './operation-detail-tabs-checks-pol.component.html',
	styleUrl: './operation-detail-tabs-checks-pol.component.scss',
})
export class OperationDetailTabsChecksPolComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	private readonly rolePermissions = inject(RolePermissionsService);
	private readonly processHelper = inject(ProcessHelperService);
	public readonly masterService = inject(MasterService);

	public checksDetail: IProcessDetailResponse | null = null;
	public hasAntifraudContent = false;
	public antifraudChecks: (CHECK | undefined)[] = [];
	public pol: IOperationPolDetail | null = null;

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.recomputeFromDetail();
	}

	private recomputeFromDetail(): void {
		const pol = this.resolvePol(this.detail);
		this.pol = pol;
		this.checksDetail = pol ? this.buildChecksDetail(pol) : null;
		this.hasAntifraudContent = this.computeHasAntifraud(pol, this.detail);
		this.antifraudChecks = this.buildAntifraudChecks(pol);
	}

	private resolvePol(detail: IOperationDetailResponse | undefined): IOperationPolDetail | null {
		if (!detail?.data) {
			return null;
		}
		return detail.data as unknown as IOperationPolDetail;
	}

	private buildChecksDetail(pol: IOperationPolDetail): IProcessDetailResponse {
		return {
			general: {
				profile: { uuid: '', alias: '' },
				sms: [],
				auth: '',
				company: '',
				company_uuid: '',
				location: [],
				devices: [],
				init_operation: '',
				total_time: 0,
				end_operation: '',
				validation: '',
			},
			process_uuid: this.detail?.process_uuid ?? '',
			process_details: '',
			status_process: PROCESS_STATUS.OK,
			data: { POL: pol } as IProcessDetailResponse['data'],
			locks: this.detail?.locks,
		} as IProcessDetailResponse;
	}

	private buildAntifraudChecks(pol: IOperationPolDetail | null): (CHECK | undefined)[] {
		return [
			this.processHelper.shouldShowOperationKoReason(pol) ? CHECK.KO_POL : undefined,
			CHECK.PROHIBITED_CAMERAS_POL,
			CHECK.FRAUD_FACE,
			CHECK.ANTISPOOF,
			this.rolePermissions.isSuperadmin() ? CHECK.ANTISPOOF_LAB : undefined,
			CHECK.FRAUD_FACE_BLOCK,
		];
	}

	private computeHasAntifraud(
		pol: IOperationPolDetail | null,
		detail: IOperationDetailResponse | undefined,
	): boolean {
		const a = pol?.antifraud;
		return !!(
			a?.PROHIBITED_CAMERAS ||
			a?.FRAUD_FACE ||
			a?.ANTISPOOF ||
			(this.rolePermissions.isSuperadmin() && a?.ANTISPOOF_LAB) ||
			(detail?.locks?.blocked_faces?.length ?? 0) > 0 ||
			pol?.actions?.KO
		);
	}
}
